/**
 * Dispute Wizard Landing Page - FULLY INTEGRATED
 * 
 * Premium guided entry point for dispute workflows.
 * Connected to real dispute generation engine.
 * 
 * Responsive: desktop / tablet / mobile
 * Theme-aware: light mode / dark mode
 * 
 * VISUAL STYLE: Matches Dashboard light/dark mode quality
 * - Light mode: soft pale green atmosphere, milky-white glass surfaces
 * - Dark mode: deep green-neutral charcoal, premium atmospheric
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useReportData } from '../../context/ReportDataContext';
import { useI18n } from '../../context/I18nContext';
import axios from 'axios';
import {
  FileText,
  Database,
  Building2,
  ChevronRight,
  Sparkles,
  Shield,
  Clock,
  Zap,
  CheckCircle,
  ArrowRight,
  RefreshCw,
  AlertTriangle,
  Scale,
  Loader2,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { metro2Service } from '../../services/metro2Service';
import { disputeIntelligenceService } from '../../services/disputeIntelligenceService';
import { buildResumeNavigation, normalizeLettersFromRoundDetail, buildClientResumeCandidate } from '../../utils/disputeWizardResume';
import ResumeLastDisputeCard from '../../components/disputes/ResumeLastDisputeCard';
import RoundEligibilityPanel from '../../components/disputes/RoundEligibilityPanel';
import CreditIntelligencePanel from '../../components/credit-report/CreditIntelligencePanel';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

// ============================================
// DISPUTE FAMILY CARD COMPONENT
// Premium card matching Dashboard quality
// White/off-white with soft emerald atmosphere
// ============================================

const DisputeFamilyCard = ({ 
  title, 
  subtitle, 
  icon: Icon, 
  iconBg,
  badge,
  badgeColor,
  useCases,
  primaryAction,
  secondaryAction,
  onPrimaryClick,
  onSecondaryClick,
  isRecommended,
  itemCount,
  isDisabled = false,
  recommendedLabel = 'AI Recommended',
  useForLabel = 'Use for',
  itemsDetectedText = null,
}) => {
  return (
    <div 
      className={`
        relative rounded-2xl p-5 sm:p-6 transition-all duration-200 
        flex flex-col min-w-0
        ${isDisabled
          ? 'bg-gradient-to-br from-stone-50 via-stone-100/50 to-stone-100/80 border border-stone-200 dark:from-[#0a1812] dark:to-[#0a1812] dark:border-stone-700/40 opacity-80'
          : isRecommended 
            ? 'bg-gradient-to-br from-white via-amber-50/30 to-amber-50/50 border-2 border-amber-300 shadow-[0_4px_24px_rgba(251,191,36,0.15)] dark:from-[#0d2018] dark:to-[#0d2018] dark:border-amber-500/40'
            : 'bg-gradient-to-br from-white via-emerald-50/20 to-emerald-50/40 border border-stone-200 shadow-[0_2px_12px_rgba(16,185,129,0.06)] hover:shadow-[0_6px_24px_rgba(16,185,129,0.12)] hover:border-emerald-200 dark:from-[#0d2018] dark:to-[#0d2018] dark:border-emerald-800/40'
        }
      `}
      data-testid={`dispute-card-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {/* Recommended badge - Positioned clearly, not clipped */}
      {isRecommended && !isDisabled && (
        <div className="absolute -top-3.5 left-5 z-10">
          <span className="
            inline-flex items-center gap-1.5
            px-3 py-1.5 rounded-full
            bg-gradient-to-r from-amber-500 to-amber-600
            text-white text-xs font-bold
            shadow-lg shadow-amber-500/25
            border border-amber-400/50
          ">
            <Sparkles size={12} />
            {recommendedLabel}
          </span>
        </div>
      )}

      {/* Header - responsive layout */}
      <div className={`flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4 mb-4 ${isRecommended && !isDisabled ? 'mt-3' : 'mt-1'}`}>
        <div className={`w-11 h-11 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm ${iconBg}`}>
          <Icon size={22} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-start gap-2 mb-1">
            {/* Title - DARK text on light surface */}
            <h3 className={`text-base sm:text-lg font-bold leading-tight ${isDisabled ? 'text-stone-500 dark:text-stone-400' : 'text-stone-800 dark:text-white'}`}>{title}</h3>
            {badge && (
              <Badge variant="outline" className={`${badgeColor} text-xs whitespace-nowrap flex-shrink-0 font-semibold`}>
                {badge}
              </Badge>
            )}
          </div>
          {/* Subtitle - readable dark text */}
          <p className={`text-sm leading-relaxed ${isDisabled ? 'text-stone-500 dark:text-stone-400' : 'text-stone-600 dark:text-emerald-100'}`}>{subtitle}</p>
          {itemCount > 0 && !isDisabled && itemsDetectedText && (
            <p className="text-xs text-emerald-700 dark:text-emerald-400 mt-1.5 font-semibold">
              {itemsDetectedText}
            </p>
          )}
        </div>
      </div>

      {/* Use cases */}
      <div className="mb-5 flex-1">
        <p className={`text-xs uppercase tracking-wide mb-2 font-semibold ${isDisabled ? 'text-stone-400 dark:text-stone-500' : 'text-stone-500 dark:text-emerald-300'}`}>{useForLabel}</p>
        <ul className="space-y-1.5">
          {useCases.map((useCase, index) => (
            <li key={index} className={`flex items-start gap-2 text-sm ${isDisabled ? 'text-stone-500 dark:text-stone-400' : 'text-stone-700 dark:text-emerald-100'}`}>
              <CheckCircle size={14} className={`mt-0.5 flex-shrink-0 ${isDisabled ? 'text-stone-400 dark:text-stone-500' : 'text-emerald-500'}`} />
              <span className="leading-snug">{useCase}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Actions - responsive: side-by-side on desktop, stacked on mobile/tablet when needed */}
      <div className="
        grid gap-2.5 w-full mt-auto
        grid-cols-1 xs:grid-cols-2
        sm:grid-cols-1 md:grid-cols-2
        lg:grid-cols-1 xl:grid-cols-2
      ">
        <Button 
          onClick={onPrimaryClick}
          disabled={isDisabled}
          className={`
            w-full min-h-[44px] px-3 py-2.5
            text-sm font-semibold
            whitespace-normal text-center leading-tight
            ${isDisabled 
              ? 'bg-stone-300 dark:bg-stone-700 text-stone-500 dark:text-stone-400 cursor-not-allowed hover:bg-stone-300 dark:hover:bg-stone-700'
              : 'bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white shadow-sm'
            }
          `}
          data-testid={`btn-${primaryAction.toLowerCase().replace(/\s+/g, '-')}`}
        >
          <span className="truncate">{primaryAction}</span>
          {!isDisabled && <ChevronRight size={16} className="ml-1 flex-shrink-0" />}
        </Button>
        {secondaryAction && (
          <Button 
            variant="outline" 
            onClick={onSecondaryClick}
            disabled={isDisabled}
            className={`
              w-full min-h-[44px] px-3 py-2.5
              text-sm font-semibold
              whitespace-normal text-center leading-tight
              ${isDisabled
                ? 'border-stone-300 dark:border-stone-600 text-stone-400 dark:text-stone-500 cursor-not-allowed'
                : 'border-stone-300 text-stone-700 hover:bg-stone-50 hover:border-stone-400 dark:border-emerald-700/40 dark:text-emerald-200 dark:hover:bg-emerald-900/30 dark:hover:border-emerald-600/50'
              }
            `}
            data-testid={`btn-${secondaryAction.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <span className="truncate">{secondaryAction}</span>
          </Button>
        )}
      </div>
    </div>
  );
};

// ============================================
// MAIN LANDING PAGE
// ============================================

const DisputeWizardLanding = ({ agencyContext = null }) => {
  const navigate = useNavigate();
  const { user, token, getAuthHeaders } = useAuth();
  const { reportData } = useReportData();
  const { t } = useI18n();
  const dw = 'disputeWizardLanding';

  const itemsDetectedLabel = (count) =>
    count === 1
      ? t(`${dw}.itemsDetected`, { count: String(count) })
      : t(`${dw}.itemsDetected_plural`, { count: String(count) });
  const [aiRecommendation, setAiRecommendation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analysisResults, setAnalysisResults] = useState(null);
  const [hasBankruptcy, setHasBankruptcy] = useState(false);
  const [resumeCandidate, setResumeCandidate] = useState(null);
  const [completedPackage, setCompletedPackage] = useState(null);
  const [resumeLoading, setResumeLoading] = useState(true);
  const [resuming, setResuming] = useState(false);
  
  // Agency context handling
  const isAgencyMode = agencyContext?.isAgencyMode === true;
  const targetUserId = agencyContext?.targetUserId;
  const agencyId = agencyContext?.agencyId;

  // Analyze report on load
  useEffect(() => {
    analyzeReport();
  }, [reportData]);

  // Fetch unfinished dispute for resume
  useEffect(() => {
    const loadResumeCandidate = async () => {
      if (!token) {
        setResumeLoading(false);
        return;
      }
      try {
        const result = await disputeIntelligenceService.getResumeCandidate(token);
        if (result?.has_resume && result?.candidate) {
          setResumeCandidate(result.candidate);
          setCompletedPackage(null);
        } else {
          const clientCandidate = buildClientResumeCandidate();
          if (clientCandidate) {
            setResumeCandidate(clientCandidate);
            setCompletedPackage(null);
          } else {
            setResumeCandidate(null);
            setCompletedPackage(result?.completed_package || null);
          }
        }
      } catch (err) {
        console.warn('[DisputeWizardLanding] resume candidate fetch failed', err);
        const clientCandidate = buildClientResumeCandidate();
        setResumeCandidate(clientCandidate);
        setCompletedPackage(null);
      } finally {
        setResumeLoading(false);
      }
    };
    loadResumeCandidate();
  }, [token, targetUserId]);

  const handlePreviewLetters = async () => {
    if (!resumeCandidate?.round_id || !token) return;
    navigate('/disputes', { state: { previewRoundId: resumeCandidate.round_id } });
  };

  const handleResumeLastDispute = async () => {
    if (!token || (!resumeCandidate?.round_id && !resumeCandidate?.package_id)) return;
    setResuming(true);
    try {
      let navigation;
      if (!resumeCandidate.round_id && resumeCandidate.package_id) {
        navigation = {
          path: `/dispute-wizard/final-package?package_id=${encodeURIComponent(resumeCandidate.package_id)}`,
          state: {
            resume: true,
            packageId: resumeCandidate.package_id,
            package_id: resumeCandidate.package_id,
          },
          wizardStep: resumeCandidate.wizard_step || resumeCandidate.current_step || 'supporting_documents',
        };
      } else if (resumeCandidate.source === 'client_storage' && !resumeCandidate.wizard_navigation_state) {
        try {
          const detail = await metro2Service.getRound(resumeCandidate.round_id, token);
          navigation = buildResumeNavigation(detail, resumeCandidate);
        } catch (fetchErr) {
          console.warn('[DisputeWizardLanding] round fetch for client resume failed', fetchErr);
          navigation = {
            path: resumeCandidate.resume_path || '/dispute-wizard/factual/select-items',
            state: { resume: true, roundId: resumeCandidate.round_id, packageId: resumeCandidate.package_id },
            wizardStep: resumeCandidate.wizard_step || resumeCandidate.current_step,
          };
        }
      } else {
        const detail = await metro2Service.getRound(resumeCandidate.round_id, token);
        navigation = buildResumeNavigation(detail, resumeCandidate);
      }
      const lettersInNav = navigation?.state?.letterPackage?.letters?.length || 0;
      const lettersInRound =
        resumeCandidate.letter_count ||
        normalizeLettersFromRoundDetail(detail).length ||
        0;
      const resumeStepsWithoutLetters = ['select_items'];
      if (
        lettersInNav === 0 &&
        lettersInRound === 0 &&
        !resumeStepsWithoutLetters.includes(navigation.wizardStep)
      ) {
        toast.error(t(`${dw}.resumeNoLetters`));
        setResuming(false);
        return;
      }
      navigate(navigation.path, { state: navigation.state });
    } catch (err) {
      console.error('[DisputeWizardLanding] resume failed', err);
      toast.error(t(`${dw}.resumeFailed`));
    } finally {
      setResuming(false);
    }
  };

  const analyzeReport = async () => {
    setLoading(true);
    
    try {
      // Count negative items
      const negativeAccounts = reportData?.tradelines?.filter(t => 
        t.is_negative || 
        t.payment_status?.toLowerCase().includes('late') ||
        t.payment_status?.toLowerCase().includes('collection') ||
        t.payment_status?.toLowerCase().includes('charge')
      ) || [];
      
      const hardInquiries = reportData?.inquiries?.filter(i => 
        i.inquiry_type === 'hard' || !i.inquiry_type
      ) || [];
      
      const publicRecords = reportData?.public_records || [];
      
      // Check for bankruptcy
      const bankruptcyRecords = publicRecords.filter(pr => 
        pr.type?.toLowerCase().includes('bankruptcy') ||
        pr.description?.toLowerCase().includes('bankruptcy')
      );
      
      const hasBankruptcyFlag = bankruptcyRecords.length > 0 || 
        negativeAccounts.some(a => 
          a.remarks?.toLowerCase().includes('bankruptcy') ||
          a.payment_status?.toLowerCase().includes('bankruptcy')
        );
      
      setHasBankruptcy(hasBankruptcyFlag);
      
      // Check for Metro 2 issues
      const hasMetro2Issues = negativeAccounts.some(t => 
        t.metro2_conflicts?.length > 0 || 
        t.field_inconsistencies?.length > 0 ||
        t.status_code_conflict ||
        t.balance_conflict
      );
      
      // Set analysis results
      setAnalysisResults({
        negativeAccounts: negativeAccounts.length,
        hardInquiries: hardInquiries.length,
        publicRecords: publicRecords.length,
        bankruptcyRecords: bankruptcyRecords.length,
        hasMetro2Issues,
        hasBankruptcy: hasBankruptcyFlag,
      });
      
      // Determine recommendation
      let recommendation = 'factual';
      
      if (hasBankruptcyFlag) {
        recommendation = 'bankruptcy';
      } else if (hasMetro2Issues) {
        recommendation = 'metro2';
      } else if (negativeAccounts.length > 0 || hardInquiries.length > 0) {
        recommendation = 'factual';
      }
      
      setAiRecommendation(recommendation);
      
    } catch (error) {
      console.error('Analysis error:', error);
      setAiRecommendation('factual');
    } finally {
      setLoading(false);
    }
  };

  // Navigation handlers - these now go to the integrated generation flow
  const handleFactualRounds = () => {
    navigate('/dispute-wizard/factual/select-items?mode=rounds');
  };
  
  const handleFactualAnytime = () => {
    navigate('/dispute-wizard/factual/select-items?mode=anytime');
  };
  
  const handleMetro2Rounds = () => {
    // Navigate to the isolated Metro 2 Technical Wizard
    navigate('/dispute-wizard/metro2');
  };
  
  const handleMetro2Anytime = () => {
    // Navigate to the isolated Metro 2 Technical Wizard
    navigate('/dispute-wizard/metro2');
  };
  
  const handleAlternateBureau = () => {
    navigate('/dispute-wizard/alternate-bureaus');
  };
  
  const handleFreezeOptOut = () => {
    navigate('/dispute-wizard/alternate-bureaus?mode=freeze-optout');
  };
  
  const handleBankruptcyReview = () => {
    navigate('/dispute-wizard/bankruptcy-review');
  };

  const isAutoStarter = user?.tier === 'auto_starter' || !user?.tier;

  return (
    <div className="min-h-screen transition-colors" data-testid="dispute-wizard-landing">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center flex-shrink-0 shadow-sm">
              <Sparkles size={20} className="text-white" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-stone-900 dark:text-emerald-50">{t(`${dw}.title`)}</h1>
              <p className="text-sm text-stone-600 dark:text-emerald-300/70">{t(`${dw}.subtitle`)}</p>
            </div>
          </div>
        </div>

        {/* Resume Last Dispute — show before hero when user has in-progress work */}
        {!loading && (
          <ResumeLastDisputeCard
            candidate={resumeCandidate}
            completedPackage={completedPackage}
            loading={resumeLoading}
            resuming={resuming}
            onResume={handleResumeLastDispute}
            onPreviewLetters={handlePreviewLetters}
            t={t}
            dw={dw}
          />
        )}

        {/* Hero Banner - Premium white with emerald atmosphere like Dashboard */}
        <div 
          className="
            rounded-2xl p-5 sm:p-6 mb-6 sm:mb-8 
            relative overflow-hidden
            border border-stone-200
            shadow-[0_4px_24px_rgba(16,185,129,0.08)]
            bg-gradient-to-br from-white via-emerald-50/30 to-emerald-50/50
            dark:from-[#0d2018] dark:to-[#0d2018] dark:border-emerald-800/30
          "
        >
          {/* Decorative atmospheric glow */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div className="absolute top-0 right-0 w-72 h-72 bg-gradient-to-bl from-emerald-200/30 to-transparent rounded-full blur-3xl -translate-y-1/3 translate-x-1/4"></div>
            <div className="absolute bottom-0 left-0 w-56 h-56 bg-gradient-to-tr from-amber-200/20 to-transparent rounded-full blur-3xl translate-y-1/3 -translate-x-1/4"></div>
          </div>
          
          <div className="relative z-10 flex flex-col lg:flex-row lg:items-start lg:justify-between gap-5 sm:gap-6">
            <div className="flex-1 min-w-0">
              {/* Badge - high contrast chip */}
              <span className="
                inline-flex items-center gap-1.5
                px-3 py-1.5 rounded-full mb-3
                bg-emerald-100 text-emerald-700 border border-emerald-200
                text-xs font-semibold
              ">
                <Zap size={12} />
                {t(`${dw}.aiPoweredAnalysis`)}
              </span>
              {/* Title - DARK text */}
              <h2 className="text-lg sm:text-xl font-bold mb-2 text-stone-800 dark:text-white">
                {t(`${dw}.heroTitle`)}
              </h2>
              {/* Body text - readable dark text */}
              <p className="text-stone-600 dark:text-emerald-100 text-sm max-w-2xl leading-relaxed">
                {t(`${dw}.heroBody`)}
              </p>
              
              {/* Feature pills - dark readable text */}
              <div className="flex flex-wrap gap-3 sm:gap-4 mt-4">
                <div className="flex items-center gap-2 text-sm text-stone-700 dark:text-emerald-100">
                  <Shield size={16} className="text-emerald-500 flex-shrink-0" />
                  <span className="font-medium">{t(`${dw}.realGeneration`)}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-stone-700 dark:text-emerald-100">
                  <Clock size={16} className="text-amber-500 flex-shrink-0" />
                  <span className="font-medium">{t(`${dw}.guidedWorkflow`)}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-stone-700 dark:text-emerald-100">
                  <CheckCircle size={16} className="text-emerald-500 flex-shrink-0" />
                  <span className="font-medium">{t(`${dw}.downloadSend`)}</span>
                </div>
              </div>
            </div>
            
            {/* Quick stats - premium mini cards */}
            {analysisResults && (
              <div className="flex sm:flex-col gap-3 sm:gap-2.5 sm:min-w-[160px] lg:min-w-[180px]">
                <div className="
                  flex-1 sm:flex-none rounded-xl p-3
                  bg-white/80 border border-emerald-200/60
                  shadow-sm
                ">
                  <div className="text-xl sm:text-2xl font-bold text-emerald-600">
                    {analysisResults.negativeAccounts}
                  </div>
                  <div className="text-xs text-stone-600 font-medium">{t(`${dw}.negativeItems`)}</div>
                </div>
                <div className="
                  flex-1 sm:flex-none rounded-xl p-3
                  bg-white/80 border border-amber-200/60
                  shadow-sm
                ">
                  <div className="text-xl sm:text-2xl font-bold text-amber-600">
                    {analysisResults.hardInquiries}
                  </div>
                  <div className="text-xs text-stone-600 font-medium">{t(`${dw}.hardInquiries`)}</div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Bankruptcy Alert Banner - amber warmth */}
        {hasBankruptcy && !loading && (
          <div className="
            bg-amber-50 border border-amber-200
            dark:bg-amber-900/20 dark:border-amber-700/40
            rounded-xl p-4 sm:p-5 mb-6
          ">
            <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4">
              <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-800/40 flex items-center justify-center flex-shrink-0">
                <Scale size={20} className="text-amber-600 dark:text-amber-400" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-amber-900 dark:text-amber-200 mb-1">{t(`${dw}.bankruptcyDetected`)}</h3>
                <p className="text-sm text-amber-800 dark:text-amber-300/80 mb-3 leading-relaxed">
                  {t(`${dw}.bankruptcyBody`)}
                </p>
                <Button 
                  onClick={handleBankruptcyReview}
                  className="bg-amber-600 hover:bg-amber-700 text-white w-full sm:w-auto font-semibold"
                >
                  <Scale size={16} className="mr-2" />
                  {t(`${dw}.startBankruptcyReview`)}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <Loader2 size={32} className="animate-spin text-emerald-500 mx-auto mb-3" />
              <p className="text-stone-600 dark:text-emerald-300/80">{t(`${dw}.analyzingReport`)}</p>
            </div>
          </div>
        )}

        {!loading && token && (
          <div className="space-y-6 mb-6 sm:mb-8">
            <RoundEligibilityPanel token={token} t={t} />
            <CreditIntelligencePanel token={token} t={t} />
          </div>
        )}

        {/* Main Cards Grid - Responsive */}
        {!loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6 mb-6 sm:mb-8">
            {/* Card 1: Factual Dispute Letters */}
            <DisputeFamilyCard
              title={t(`${dw}.factualTitle`)}
              subtitle={t(`${dw}.factualSubtitle`)}
              icon={FileText}
              iconBg="bg-gradient-to-br from-emerald-500 to-emerald-600"
              badge={t(`${dw}.standardFlow`)}
              badgeColor="
                bg-emerald-100 text-emerald-700 border-emerald-200 font-semibold
                dark:bg-emerald-900/40 dark:text-emerald-300 dark:border-emerald-700/40
              "
              useCases={[
                t(`${dw}.factualUse1`),
                t(`${dw}.factualUse2`),
                t(`${dw}.factualUse3`),
                t(`${dw}.factualUse4`),
              ]}
              primaryAction={t(`${dw}.buildRoundsLetters`)}
              secondaryAction={t(`${dw}.buildAnytimeLetters`)}
              onPrimaryClick={handleFactualRounds}
              onSecondaryClick={handleFactualAnytime}
              isRecommended={aiRecommendation === 'factual'}
              itemCount={analysisResults?.negativeAccounts || 0}
              recommendedLabel={t(`${dw}.aiRecommended`)}
              useForLabel={t(`${dw}.useFor`)}
              itemsDetectedText={
                (analysisResults?.negativeAccounts || 0) > 0
                  ? itemsDetectedLabel(analysisResults.negativeAccounts)
                  : null
              }
            />

            {/* Card 2: Metro 2 Dispute Letters - ONLY for Agency Mode */}
            {isAgencyMode && (
              <DisputeFamilyCard
                title={t(`${dw}.metro2Title`)}
                subtitle={t(`${dw}.metro2Subtitle`)}
                icon={Database}
                iconBg="bg-gradient-to-br from-emerald-500 to-emerald-600"
                badge={t(`${dw}.technicalReview`)}
                badgeColor="
                  bg-emerald-100 text-emerald-700 border-emerald-200 font-semibold
                  dark:bg-emerald-900/40 dark:text-emerald-300 dark:border-emerald-700/40
                "
                useCases={[
                  t(`${dw}.metro2Use1`),
                  t(`${dw}.metro2Use2`),
                  t(`${dw}.metro2Use3`),
                  t(`${dw}.metro2Use4`),
                ]}
                primaryAction={t(`${dw}.buildRoundsLetters`)}
                secondaryAction={t(`${dw}.buildAnytimeLetters`)}
                onPrimaryClick={handleMetro2Rounds}
                onSecondaryClick={handleMetro2Anytime}
                isRecommended={aiRecommendation === 'metro2'}
                itemCount={analysisResults?.hasMetro2Issues ? analysisResults?.negativeAccounts : 0}
                recommendedLabel={t(`${dw}.aiRecommended`)}
                useForLabel={t(`${dw}.useFor`)}
                itemsDetectedText={
                  analysisResults?.hasMetro2Issues && (analysisResults?.negativeAccounts || 0) > 0
                    ? itemsDetectedLabel(analysisResults.negativeAccounts)
                    : null
                }
              />
            )}

            {/* Card 3: Alternate Bureau Letters - Coming Soon for Self-Repair */}
            <DisputeFamilyCard
              title={t(`${dw}.alternateTitle`)}
              subtitle={isAgencyMode 
                ? t(`${dw}.alternateSubtitleAgency`)
                : t(`${dw}.alternateSubtitleSelf`)
              }
              icon={Building2}
              iconBg={isAgencyMode 
                ? "bg-gradient-to-br from-amber-500 to-amber-600"
                : "bg-gradient-to-br from-stone-400 to-stone-500"
              }
              badge={isAgencyMode ? t(`${dw}.sourceFirst`) : t('common.comingSoon')}
              badgeColor={isAgencyMode 
                ? "bg-amber-100 text-amber-700 border-amber-200 font-semibold dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-700/40"
                : "bg-stone-100 text-stone-600 border-stone-300 font-semibold dark:bg-stone-800/60 dark:text-stone-400 dark:border-stone-600/40"
              }
              useCases={isAgencyMode ? [
                t(`${dw}.alternateUseAgency1`),
                t(`${dw}.alternateUseAgency2`),
                t(`${dw}.alternateUseAgency3`),
                t(`${dw}.alternateUseAgency4`),
              ] : [
                t(`${dw}.alternateUseSelf1`),
                t(`${dw}.alternateUseSelf2`),
                t(`${dw}.alternateUseSelf3`),
                t(`${dw}.alternateUseSelf4`),
              ]}
              primaryAction={isAgencyMode ? t(`${dw}.buildAlternateBureauLetters`) : t('common.comingSoon')}
              secondaryAction={isAgencyMode ? t(`${dw}.freezeOptOutLetters`) : null}
              onPrimaryClick={isAgencyMode ? handleAlternateBureau : () => toast.info(t(`${dw}.alternateComingSoonToast`))}
              onSecondaryClick={isAgencyMode ? handleFreezeOptOut : null}
              isRecommended={false}
              itemCount={0}
              isDisabled={!isAgencyMode}
              recommendedLabel={t(`${dw}.aiRecommended`)}
              useForLabel={t(`${dw}.useFor`)}
            />
          </div>
        )}

        {/* Auto Starter Info Banner - Premium white with emerald accent */}
        {isAutoStarter && !loading && (
          <div 
            className="
              rounded-xl p-4 sm:p-5 flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4
              border border-emerald-200
              shadow-sm
              bg-gradient-to-br from-white via-emerald-50/30 to-emerald-50/50
              dark:from-[#0d2018] dark:to-[#0d2018] dark:border-emerald-800/30
            "
          >
            <div className="
              w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0
              bg-emerald-100 border border-emerald-200
            ">
              <Shield size={20} className="text-emerald-600" />
            </div>
            <div className="min-w-0">
              {/* Title - DARK text */}
              <h3 className="font-semibold text-stone-800 dark:text-white mb-1">{t(`${dw}.autoStarterTitle`)}</h3>
              {/* Body - readable dark text */}
              <p className="text-sm text-stone-600 dark:text-emerald-100 leading-relaxed">
                {t(`${dw}.autoStarterBody`)}
              </p>
            </div>
          </div>
        )}

        {/* Bottom Action Bar */}
        {!loading && (
          <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/disputes')}
              className="text-stone-600 hover:text-stone-800 dark:text-emerald-400/80 dark:hover:text-emerald-300 w-full sm:w-auto justify-center sm:justify-start font-medium"
            >
              {t(`${dw}.backToDisputeCenter`)}
            </Button>
            <div className="flex items-center justify-center sm:justify-end gap-2 text-sm text-stone-600 dark:text-emerald-400/80">
              <CheckCircle size={14} className="text-emerald-500 dark:text-emerald-400" />
              <span className="font-medium">{t(`${dw}.fullyIntegrated`)}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DisputeWizardLanding;
